import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as transactionService from "@/shared/api/services/transactionService";
import * as merchantService from "@/shared/api/services/merchantService";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Calendar } from "@/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Search,
  Calendar as CalendarIcon,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Globe,
  Filter,
  X,
} from "lucide-react";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function MerchantTransactions() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    type: "all",
    dateFrom: null,
    dateTo: null,
  });
  const [search, setSearch] = useState("");
  const [selectedTx, setSelectedTx] = useState(null);
  const [page, setPage] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pageSize = 10;

  const { data: profile } = useQuery({
    queryKey: ["merchant-profile"],
    queryFn: () => merchantService.getProfile(),
  });
  const merchantId = profile?.id;

  const {
    data: paginationResult,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: [
      "merchant-transactions",
      page,
      pageSize,
      search,
      filters,
      merchantId,
    ],
    queryFn: () => {
      const backendFilters = {};
      if (filters.status !== "all") backendFilters.status = filters.status;
      if (filters.type !== "all") backendFilters.type = filters.type;
      if (filters.dateFrom)
        backendFilters.startDate = filters.dateFrom.toISOString();
      if (filters.dateTo) {
        const endOfDay = new Date(filters.dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        backendFilters.endDate = endOfDay.toISOString();
      }
      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        search,
        backendFilters,
        merchantId ? [merchantId] : [],
      );
    },
    enabled: !!merchantId,
    keepPreviousData: true,
  });

  const transactions = paginationResult?.data || [];
  const totalTransactions = paginationResult?.total || 0;
  const totalPages = paginationResult?.totalPages || 1;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (err) {
      console.error("Error refreshing data:", err);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      type: "all",
      dateFrom: null,
      dateTo: null,
    });
    setSearch("");
    setPage(0);
  };

  const handleExport = () => {
    const headers = [
      t("type"),
      t("transactionId"),
      t("provider"),
      t("amount"),
      t("status"),
      t("date"),
    ];
    const rows = transactions.map((tx) => [
      tx.type,
      tx.id,
      tx.provider?.name || tx.provider_code || "",
      (tx.amount ?? 0).toString(),
      tx.status,
      tx.createdAt,
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `merchant-transactions-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const customDateRangeLabel =
    filters.dateFrom || filters.dateTo
      ? `${filters.dateFrom ? format(filters.dateFrom, "MMM d, yyyy") : "Start"} - ${filters.dateTo ? format(filters.dateTo, "MMM d, yyyy") : "End"}`
      : null;

  const hasActiveFilters =
    Object.entries(filters).some(
      ([k, v]) => v && v !== "all" && k !== "search",
    ) || !!search;

  return (
    <MerchantLayout currentPage="MerchantTransactions">
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-medium">
              {t("errorLoadingTransactions") || "Error loading transactions"}
            </p>
            <p className="text-red-600 text-sm mt-1">
              {error.message || t("pleaseCheckConnection")}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Globe className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {t("transactions")}
              </h1>
              <p className="text-slate-500">
                {isLoading ? (
                  <Skeleton width={150} />
                ) : (
                  `${totalTransactions.toLocaleString()} ${t("transactionsFound")}`
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              {t("refresh")}
            </Button>
            <Button variant="outline" onClick={handleExport} className="gap-2">
              <Download className="h-4 w-4" />
              {t("export")}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="font-medium text-slate-700">{t("filters")}</span>
            </div>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="gap-1 text-slate-500"
              >
                <X className="h-4 w-4" />
                {t("clearAll")}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative lg:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t("searchHeaderPlaceholder")}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                className="pl-10"
              />
            </div>

            {/* Type Filter */}
            <Select
              value={filters.type}
              onValueChange={(v) => {
                setFilters({ ...filters, type: v });
                setPage(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("allTypes") || "All types"} />
              </SelectTrigger>
              <SelectContent>
                {[
                  { value: "all", label: t("allTypes") || "All types" },
                  { value: "deposit", label: t("deposit") },
                  { value: "withdrawal", label: t("withdrawal") },
                ].map((item) => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
                  >
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select
              value={filters.status}
              onValueChange={(v) => {
                setFilters({ ...filters, status: v });
                setPage(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("allStatus")} />
              </SelectTrigger>
              <SelectContent>
                {[
                  { value: "all", label: t("allStatus") },
                  { value: "pending", label: t("pending") },
                  { value: "declined", label: t("Declined") || "Declined" },
                  { value: "success", label: t("success") },
                ].map((item) => (
                  <SelectItem
                    key={item.value}
                    value={item.value}
                    className="cursor-pointer focus:bg-slate-100 focus:text-slate-900"
                  >
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Custom Date Range */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-start text-left font-normal !border-slate-200 hover:!border-slate-200 focus-visible:!border-slate-200 focus-visible:!ring-0 focus-visible:!ring-offset-0 hover:bg-transparent hover:text-current data-[state=open]:bg-transparent data-[state=open]:text-current data-[state=open]:!border-slate-200"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  <span
                    className={
                      customDateRangeLabel ? "text-slate-900" : "text-slate-700"
                    }
                  >
                    {customDateRangeLabel ||
                      t("customDateRange") ||
                      t("dateRange") ||
                      "Custom Date Range"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{
                    from: filters.dateFrom || undefined,
                    to: filters.dateTo || undefined,
                  }}
                  onSelect={(range) => {
                    setFilters({
                      ...filters,
                      dateFrom: range?.from || null,
                      dateTo: range?.to || null,
                    });
                    setPage(0);
                  }}
                  disabled={{ after: new Date() }}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Transactions Table */}
        <TransactionTable
          transactions={transactions}
          isLoading={isLoading}
          onRowClick={setSelectedTx}
          defaultCurrency={profile?.currency || "PKR"}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {t("showing")} {page * pageSize + 1} {t("to")}{" "}
              {Math.min((page + 1) * pageSize, totalTransactions)} {t("of")}{" "}
              {totalTransactions.toLocaleString()} {t("transactions")}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">
                {t("page")} {page + 1} {t("of")} {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        defaultCurrency={selectedTx?.currency || profile?.currency || "PKR"}
      />
    </MerchantLayout>
  );
}
